<template>
  <div id="container">
    <div id="title-panel">
      <div id="title-container">
        <h2>{{ t('supplierDetails.coworkers.coworkers') }}</h2>
        <div id="coworkers-counter">
          {{ supplierData?.coworkers.length ?? 0 }}
        </div>
      </div>
      <p id="description">
        {{ t('supplierDetails.coworkers.description') }}
      </p>
      <vf-button
        id="add-coworker-button"
        :size="buttonSizes.large"
        :variant="buttonVariants.secondary"
        @click="openAddCoworkerModal"
      >
        {{ t('supplierDetails.coworkers.addCoworker') }}
      </vf-button>
    </div>
    <table-wrapper
      v-model:page-options="pageOptions"
      v-model:filters="filters"
      action-icon="delete"
      :fields="tableFields"
      :filter-configuration="filterConfiguration"
      :items="coworkers"
      :loading="isLoading"
      :mode="TableMode.CLIENT_SIDE"
      @action-icon-click="deleteCoworkerFromProject"
    ></table-wrapper>

    <ad-group-modal
      ref="modalRef"
      :group="AdGroup.COWORKER"
      :title="t('supplierDetails.coworkers.addCoworker')"
      @selected-user="handleAddCoworker"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import axios, { isAxiosError } from 'axios';
  import endpointsList from '../../helpers/endpointsList';
  import SpGroupModal from '../SpGroupModal/SpGroupModal.vue';
  import { IAdUserInternalFormat, ICoworker, ISupplier } from 'types';
  import {
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '@/components/TableWrapper';
  import { ref } from 'vue';
  import AdGroupModal from '../AdGroupModal/AdGroupModal.vue';
  import { AdGroup } from 'types';

  const { t } = useI18n();
  const { createSuccessToast, createErrorToast, createWarningToast } =
    useToast();

  const props = defineProps<{ supplierData: ISupplier }>();

  const { pageOptions, filters, isLoading } = useTableWrapper<ICoworker>(
    TableMode.CLIENT_SIDE,
    'lastName',
    getCoworkers,
  );

  const coworkers = ref<IAdUserInternalFormat[]>([]);

  const filterConfiguration = new TableFiltersBuilder<ICoworker>({
    translationKey: 'supplierDetails.coworkers',
  })
    .addInputFilter('firstName', { width: '208px' })
    .addInputFilter('lastName', { width: '208px' })
    .addInputFilter('email', { width: '208px' })
    .build();
  const tableFields = computed(() =>
    new TableFieldsBuilder<ICoworker>({
      sortable: true,
      labelTranslationKey: 'supplierDetails.coworkers',
    })
      .addField('firstName', { width: '36%' })
      .addField('lastName', { width: '28%' })
      .addField('email', { width: '34%' })
      .build(),
  );

  async function getCoworkers(): Promise<void> {
    isLoading.value = true;
    try {
      const response = await axios.get(
        endpointsList.suppliers.getCoworkersForSupplier(
          props.supplierData._id.toString(),
        ),
      );
      coworkers.value = response.data;
    } catch {
      createErrorToast(t('supplierDetailView.errors.couldNotGetCoworkers'));
    } finally {
      isLoading.value = false;
    }
  }

  async function handleAddCoworker(coworker: ICoworker) {
    if (!props.supplierData) {
      return;
    }
    try {
      await axios.patch(
        endpointsList.suppliers.addCoworkerToSupplier(
          props.supplierData._id.toString(),
          coworker.oid,
        ),
      );
      createSuccessToast(t('supplierDetails.coworkerAdded'));
      getCoworkers();
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        createErrorToast(
          t('supplierDetails.coworkers.errors.coworkerAlreadyAddedLong', {
            supplierName: err.response.data.message.nameOfSupplierWithCoworker,
          }),
        );
      } else {
        createErrorToast(
          t('supplierDetails.coworkers.errors.couldNotAddCoworker'),
        );
      }
    }
  }
  async function deleteCoworkerFromProject() {
    createWarningToast(t('generic.notImplemented'));
  }

  const modalRef = ref<InstanceType<typeof SpGroupModal> | null>(null);
  function openAddCoworkerModal() {
    modalRef.value?.show();
  }
</script>

<style scoped lang="scss">
  #container {
    margin-top: 34px;

    #title-panel {
      margin-bottom: 50px;
      width: 100%;
      display: grid;
      grid-template-columns: 1fr 2fr 1fr;
      align-items: center;
      justify-items: center;

      #title-container {
        justify-self: flex-start;
        display: flex;
        align-items: flex-start;
        column-gap: 0.625rem;

        h2 {
          font-size: $subheading-font-size;
          font-weight: $subheading-font-weight;
        }

        #coworkers-counter {
          background-color: black;
          color: white;
          font-size: 12px;
          border-radius: 50px;
          padding: 3px 20px;
        }
      }

      p#description {
        margin-bottom: 0;
        max-height: 62px;
        width: 645px;
        font-size: 16px;
        line-height: 28px;
        font-weight: 500;
      }

      button#add-coworker-button {
        justify-self: end;
      }
    }
  }
</style>
